import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { StoreProvider, useStore } from './lib/store'
import { ThemeProvider } from './theme/ThemeProvider'
import { BottomNav, OfflineBar } from './ui/Nav'
import { ShoutButton } from './ui/Shout'
import { SkeletonCard, Spacer, Toast } from './ui'

import Home from './screens/Home'
import Attendance from './screens/Attendance'
import Schedule from './screens/Schedule'
import Game from './screens/Game'
import Food from './screens/Food'
import Fund from './screens/Fund'
import Challenges from './screens/Challenges'
import ChallengeDetail from './screens/ChallengeDetail'
import PicksEdit from './screens/PicksEdit'
import PicksBoard from './screens/PicksBoard'
import PicksCompare from './screens/PicksCompare'
import Standings from './screens/Standings'
import CheckIn from './screens/CheckIn'
import Online from './screens/Online'
import Shouts from './screens/Shouts'
import Timeline from './screens/Timeline'
import Settings from './screens/Settings'
import Profile from './screens/Profile'
import Join from './screens/Join'
import ProfileSetup from './screens/ProfileSetup'
import Verify from './screens/Verify'
import SignIn from './screens/SignIn'
import ClaimName from './screens/ClaimName'

const FoodArchive = lazy(() => import('./screens/FoodArchive'))
const NewChallenge = lazy(() => import('./screens/NewChallenge'))
const Admin = lazy(() => import('./screens/Admin'))
const AdminElevate = lazy(() => import('./screens/AdminElevate'))
const Legal = lazy(() => import('./screens/Legal'))

/** Routes that render without the bottom bar. */
const BARE = ['/join', '/signin', '/verify', '/setup', '/claim-name', '/terms', '/privacy', '/admin']

function Shell() {
  const { status, state, toasts, dismissToast } = useStore()
  const { pathname } = useLocation()

  if (status === 'loading') {
    return (
      <div style={{ padding: 16, paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))' }}>
        <div className="skeleton-bar" style={{ width: '45%', height: 22, marginBottom: 18 }} />
        <SkeletonCard lines={4} />
        <Spacer h={12} />
        <SkeletonCard lines={3} />
        <Spacer h={12} />
        <SkeletonCard lines={2} />
      </div>
    )
  }

  if (status === 'unauthenticated') {
    return (
      <Routes>
        <Route path="/join" element={<Join />} />
        {/* Every login email points at /signin. Without this the token in the
            query string is discarded by the catch-all below and the tap lands
            on the passcode screen. */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/verify" element={<Verify />} />
        <Route path="/setup" element={<ProfileSetup />} />
        <Route path="/terms" element={<Suspense fallback={null}><Legal doc="terms" /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={null}><Legal doc="privacy" /></Suspense>} />
        <Route path="*" element={<Navigate to="/join" replace />} />
      </Routes>
    )
  }

  if (status === 'needs-profile') {
    // Verified but nameless — a blocking picker until resolved (D115).
    return (
      <Routes>
        <Route path="*" element={<ClaimName />} />
      </Routes>
    )
  }

  if (status === 'error') {
    return (
      <div style={{ padding: 24, paddingTop: 'calc(48px + env(safe-area-inset-top, 0px))', textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>🏈</div>
        <h1 className="big-title" style={{ marginTop: 14 }}>Can’t reach the app</h1>
        <p style={{ color: 'var(--dim)', fontSize: 14 }}>
          Check your connection. Everything you did offline is saved and will send when you’re back.
        </p>
        <button
          onClick={() => location.reload()}
          style={{ marginTop: 18, padding: '14px 22px', borderRadius: 14, background: 'var(--fill)', color: 'var(--ink)', fontWeight: 700 }}
        >
          Try again
        </button>
      </div>
    )
  }

  const showNav = !BARE.some((p) => pathname.startsWith(p))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      <OfflineBar />
      <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Suspense fallback={<div style={{ padding: 16 }}><SkeletonCard lines={3} /></div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/attendance" element={<Attendance />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/game/:id" element={<Game />} />
            <Route path="/food" element={<Food />} />
            <Route path="/food/archive" element={<FoodArchive />} />
            <Route path="/fund" element={<Fund />} />
            <Route path="/challenges" element={<Challenges />} />
            <Route path="/challenges/new" element={<NewChallenge />} />
            <Route path="/challenges/:id" element={<ChallengeDetail />} />
            {/* The board took `/picks`; the selection grid moved under it. */}
            <Route path="/picks" element={<PicksBoard />} />
            <Route path="/picks/edit" element={<PicksEdit />} />
            <Route path="/picks/standings" element={<Standings />} />
            <Route path="/picks/u/:id" element={<PicksCompare />} />
            <Route path="/checkin" element={<CheckIn />} />
            <Route path="/online" element={<Online />} />
            <Route path="/shouts" element={<Shouts />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/settings" element={<Settings />} />
            {/*
              Verifying an email happens from Settings, which means it happens
              while signed in. Leaving `/verify` out of this table sent it to the
              catch-all below: Settings navigated there, React Router bounced it
              straight back to Home, and email verification was simply not
              completable. Everything downstream of a verified address — locking
              your name in, signing in on a second device, admin — was
              unreachable because of one missing line.

              `/claim-name` is here for the same reason. The D115 picker is
              usually reached by the `needs-profile` branch above, but `/verify`
              navigates to it directly on a D5 name race, and that navigation
              happens before the refresh that would flip the status.
            */}
            <Route path="/verify" element={<Verify />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/claim-name" element={<ClaimName />} />
            <Route path="/u/:id" element={<Profile />} />
            <Route path="/admin/elevate" element={<AdminElevate />} />
            <Route path="/admin/*" element={<Admin />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/join" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      {showNav ? <BottomNav /> : null}
      {/* Above the bar, on every screen that has one. */}
      <ShoutButton />
      {toasts.map((t) => (
        <Toast
          key={t.id}
          message={t.message}
          action={t.action?.label}
          onAction={t.action ? () => { t.action!.run(); dismissToast(t.id) } : undefined}
          onDismiss={() => dismissToast(t.id)}
        />
      ))}
    </div>
  )
}

function Themed() {
  const { state } = useStore()
  return (
    <ThemeProvider teamId={state?.user.teamId} textSize={state?.user.textSize ?? 'normal'}>
      <Shell />
    </ThemeProvider>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <StoreProvider>
        <Themed />
      </StoreProvider>
    </BrowserRouter>
  )
}
